import {
	type CacheConfig,
	ConnectionInterface,
	type DataID,
	DefaultHandlerProvider,
	Environment,
	type EnvironmentConfig,
	type LogFunction,
	type MutableRecordSource,
	Network,
	type OperationLoader,
	RelayFeatureFlags,
	type RelayFieldLogger,
	RecordSource as RelayRecordSource,
	type RenderPolicy,
	type RequestParameters,
	type Variables,
} from 'relay-runtime';
import LiveResolverStore, {
	type ActorIdentifier,
	type GetDataID,
	// @ts-ignore
} from 'relay-runtime/lib/store/live-resolvers/LiveResolverStore';
import {
	type HandleFieldPayload,
	type Handler,
	type RecordProxy,
	type RecordSourceProxy,
	type Scheduler,
} from 'relay-runtime/lib/store/RelayStoreTypes';
import invariant from 'tiny-invariant';
import warning from 'tiny-warning';

import RelayDataID from '@atlassian/relay-data-id';
import { type ExternalDependencies, fetch as multiFetch } from '@atlassian/relay-fetch/multi-fetch';
import RelayMissingFieldHandlers from '@atlassian/relay-missing-field-handlers';
import RelayScheduler from '@atlassian/relay-scheduler';
import { createSubscribeFn, type SubscribeOptions } from '@atlassian/relay-subscription';

// Enable Relay Resolvers globally for this module
RelayFeatureFlags.ENABLE_RELAY_RESOLVERS = true;

type NetworkOptions = ExternalDependencies & SubscribeOptions;

function createNetwork(networkOptions: NetworkOptions) {
	return Network.create(
		(request: RequestParameters, variables: Variables, cacheConfig: CacheConfig) =>
			multiFetch(request, variables, cacheConfig, networkOptions),
		networkOptions.isServer === false ? createSubscribeFn(networkOptions) : undefined,
	);
}

type Props = {
	log?: EnvironmentConfig['log'];
	requiredFieldLogger?: RelayFieldLogger;
	networkOptions?: NetworkOptions;
	/**
	 * @deprecated Do not override that option, it's here only for Townsquare backwards compatibility and will be removed soon.
	 */
	getDataID?: EnvironmentConfig['getDataID'];
	/**
	 * Returns the render policy Relay applies to a read that is missing data. Omit to keep Relay's
	 * own default of `'partial'`, which renders the incomplete result with absent fields resolved to
	 * `null`; `'full'` makes Relay suspend or fail the read instead.
	 *
	 * This is a function rather than a value because Relay's `UNSTABLE_defaultRenderPolicy` config
	 * field is captured once in the `Environment` constructor, and products need to vary the policy
	 * dynamically (e.g. for feature gating which can't be module-scoped).
	 */
	getDefaultRenderPolicy?: () => RenderPolicy;
};

export type AtlassianRelayEnvironment = Environment;

/**
 * Relay calls `environment.UNSTABLE_getDefaultRenderPolicy()` on every `QueryResource.prepare`, so
 * this allows overriding it dynamically (and not during module scope evaluation).
 */
class DynamicRenderPolicyEnvironment extends Environment {
	private readonly getRenderPolicy: () => RenderPolicy;

	constructor(config: EnvironmentConfig, getRenderPolicy: () => RenderPolicy) {
		super(config);
		this.getRenderPolicy = getRenderPolicy;
	}

	/**
	 * Note: currently missing from @types/relay-runtime (version 20.1.1) but DOES exist at runtime.
	 */
	UNSTABLE_getDefaultRenderPolicy(): RenderPolicy {
		return this.getRenderPolicy();
	}
}

const createStore = (
	source: MutableRecordSource,
	options?: {
		actorIdentifier?: ActorIdentifier;
		gcReleaseBufferSize?: number;
		gcScheduler?: Scheduler;
		getDataID?: GetDataID;
		log?: LogFunction;
		operationLoader?: OperationLoader;
		queryCacheExpirationTime?: number;
		shouldProcessClientComponents?: boolean;
		treatMissingFieldsAsNull?: boolean;
	},
) => {
	return new LiveResolverStore(source, options);
};

// Copied from ConnectionHandler.deleteNode implementation with a single change in behaviour:
// nodeID is compared to the `id` field of the node, rather than node.getDataID().
// This is a sane thing to do within the scope of a single connection even in the light of
// id collisions present in AGG for the entities of different types.
// Such change makes @deleteEdge directive usable again.
function deleteNode(record: RecordProxy, nodeID: DataID): void {
	const { EDGES, NODE } = ConnectionInterface.get();

	const edges = record.getLinkedRecords(EDGES);
	if (!edges) {
		return;
	}
	let nextEdges;
	for (let ii = 0; ii < edges.length; ii++) {
		const edge = edges[ii];
		const node = edge && edge.getLinkedRecord(NODE);
		// N.B. node.getValue('id') instead of node.getDataID()
		if (node != null && node.getValue('id') === nodeID) {
			if (nextEdges === undefined) {
				nextEdges = edges.slice(0, ii);
			}
		} else if (nextEdges !== undefined) {
			nextEdges.push(edge);
		}
	}
	if (nextEdges !== undefined) {
		record.setLinkedRecords(nextEdges, EDGES);
	}
}

// Copied from DeleteEdgeHandler implementation in Relay with a single change in behaviour:
// it calls deleteNode above instead of ConnectionHandler.deleteNode.
const DeleteEdgeHandler = {
	update: (store: RecordSourceProxy, payload: HandleFieldPayload) => {
		const record = store.get(payload.dataID);
		if (record == null) {
			return;
		}
		// @types/relay-runtime is broken, handleArgs is present in the original Flow types.
		const { connections } = (
			payload as HandleFieldPayload & { handleArgs: { connections: string[] } }
		).handleArgs;
		invariant(connections != null, 'MutationHandlers: Expected connection IDs to be specified.');
		const idOrIds = record.getValue(payload.fieldKey);
		const idList = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
		idList.forEach((id) => {
			if (typeof id === 'string') {
				for (const connectionID of connections) {
					const connection = store.get(connectionID);
					if (connection == null) {
						warning(false, `[Relay] The connection with id ${connectionID} doesn't exist.`);
						continue;
					}
					deleteNode(connection, id);
				}
			}
		});
	},
};

function HandlerProvider(handle: string): Handler {
	switch (handle) {
		case 'deleteEdge':
			return DeleteEdgeHandler;
		default:
			return DefaultHandlerProvider(handle);
	}
}

export function createRelayEnvironment(environmentOptions: Props): AtlassianRelayEnvironment;

export function createRelayEnvironment(
	environmentOptions: Omit<Props, 'networkOptions'>,
): AtlassianRelayEnvironment;

export function createRelayEnvironment({
	log,
	requiredFieldLogger,
	networkOptions,
	getDataID,
	getDefaultRenderPolicy,
}: Props): AtlassianRelayEnvironment {
	const config: EnvironmentConfig = {
		configName: 'Atlassian Shared Graphql Gateway',
		network: createNetwork(networkOptions || { isServer: false }),
		store: createStore(new RelayRecordSource(), {
			log: log ?? undefined,
			getDataID: 'getDataID' in arguments[0] ? getDataID : RelayDataID,
		}),
		log,
		relayFieldLogger: requiredFieldLogger,
		getDataID: 'getDataID' in arguments[0] ? getDataID : RelayDataID,
		scheduler: RelayScheduler,
		missingFieldHandlers: RelayMissingFieldHandlers,
		handlerProvider: HandlerProvider,
	};

	return getDefaultRenderPolicy
		? new DynamicRenderPolicyEnvironment(config, getDefaultRenderPolicy)
		: new Environment(config);
}
